import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Can } from "@/auth/permissions";
import { formatCents } from "@/services/finance";
import { useReasonDialog } from "@/shared/ui/ReasonDialog";
import { LoadingState } from "@/shared/ui/PageHeader";
import { periodLabel, platformSettlementApi, type SettlementRow, type SettlementStatus } from "@/services/platformSettlement";

/**
 * SETTLE-W1 · 财务 › 应收账款 › 「API 平台分账」（样稿 ② 视图）——按真实订单自动生成的账期，取代原来写死的演示数据。
 * 汇总四格 + 账期表 + 订单明细抽屉 + 登记到账弹窗 + 核差额（ReasonDialog，原因必填）。
 */
const STATUS_STYLE: Record<SettlementStatus, string> = {
  open: "bg-blue-50 text-blue-600",
  pending: "bg-amber-50 text-amber-600",
  overdue: "bg-rose-50 text-rose-600",
  diff: "bg-amber-50 text-amber-600",
  paid: "bg-emerald-50 text-emerald-600",
};

function statusText(r: SettlementRow): string {
  if (r.status === "overdue") return `逾期 ${r.overdue_days} 天`;
  if (r.status === "diff") return `差额 ${formatCents(Math.abs(r.diff_cents), r.currency)} 待核`;
  return r.status_label;
}

const todayPT = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });   // YYYY-MM-DD（美西）

function PaymentModal({ row, onClose }: { row: SettlementRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState((row.net_cents / 100).toFixed(2));
  const [paidAt, setPaidAt] = useState(todayPT());
  const [reference, setReference] = useState("");
  const cents = Math.round(Number(amount) * 100);
  const valid = Number.isFinite(cents) && amount.trim() !== "" && !!paidAt;
  const mut = useMutation({
    mutationFn: () => platformSettlementApi.recordPayment(row.id, { amount_cents: cents, paid_at: paidAt, reference: reference.trim() || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["finance", "platform-settlements"] }); onClose(); },
  });
  const differs = valid && cents !== row.net_cents;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" onClick={onClose} role="dialog" aria-modal="true" data-testid="payment-modal">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">登记到账 · {row.platform_label}</h2>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <p className="mt-1 text-[12.5px] text-slate-500">{periodLabel(row.period_start, row.period_end)}（美西）· 应收 <b className="text-slate-900">{formatCents(row.net_cents, row.currency)}</b></p>
        <div className="mt-4 flex flex-col gap-3 text-[13px]">
          <label className="flex flex-col gap-1">实收金额（{row.currency}）
            <input data-testid="pay-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2" />
          </label>
          <label className="flex flex-col gap-1">到账日期
            <input data-testid="pay-date" type="date" max={todayPT()} value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2" />
          </label>
          <label className="flex flex-col gap-1">平台付款单号（选填）
            <input data-testid="pay-ref" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120} className="rounded-lg border border-slate-200 px-3 py-2" />
          </label>
          {differs && <div className="rounded-lg bg-amber-50 px-3 py-2 text-[12.5px] text-amber-700">实收 ≠ 应收，差额 {formatCents(Math.abs(row.net_cents - cents), row.currency)}：登记后这一期标「差额待核」，核完填原因才算结清。</div>}
          {mut.isError && <div className="text-[12.5px] text-rose-600">{(mut.error as Error).message || "登记失败"}</div>}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">取消</button>
          <button type="button" data-testid="pay-submit" disabled={!valid || mut.isPending} onClick={() => mut.mutate()} className="rounded-lg bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{mut.isPending ? "登记中…" : "确认登记"}</button>
        </div>
      </div>
    </div>
  );
}

function OrdersDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ["finance", "platform-settlements", "orders", id], queryFn: () => platformSettlementApi.orders(id) });
  const st = q.data?.settlement;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30 animate-in fade-in" onClick={onClose} data-testid="orders-drawer">
      <div className="flex h-full w-full max-w-2xl flex-col bg-white shadow-xl animate-in slide-in-from-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">订单明细{st ? ` · ${st.platform_label}` : ""}</h2>
            {st && <div className="text-[12px] text-slate-500">{periodLabel(st.period_start, st.period_end)}（美西）· {st.order_count} 单 · 应收 {formatCents(st.net_cents, st.currency)}{st.status === "open" ? " · 本期进行中（实时）" : ""}</div>}
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-auto p-5">
          {q.isLoading ? <LoadingState /> : q.isError ? (
            <button type="button" onClick={() => q.refetch()} className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">加载失败，点击重试</button>
          ) : (
            <table className="w-full text-[12.5px]">
              <thead><tr className="border-b border-slate-200 text-left text-[11.5px] font-medium text-slate-400"><th className="py-2">订单号</th><th>类型</th><th>日期</th><th className="text-right">订单总额</th><th className="text-right">平台佣金</th><th className="text-right">计入应收</th></tr></thead>
              <tbody>
                {(q.data?.items ?? []).map((l, i) => (
                  <tr key={`${l.order_no}-${i}`} className="border-b border-slate-100 align-top last:border-0">
                    <td className="py-2 font-mono text-[12px] text-blue-600">{l.order_no}</td>
                    <td>{l.kind === "adjustment" ? <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-600">调整</span> : "订单"}</td>
                    <td className="tabular-nums">{l.basis_date ?? "—"}</td>
                    <td className="text-right tabular-nums">{l.total_cents == null ? "—" : formatCents(l.total_cents)}</td>
                    <td className="text-right tabular-nums">{l.commission_cents == null ? "—" : formatCents(l.commission_cents)}</td>
                    <td className={`text-right font-semibold tabular-nums ${l.amount_cents < 0 ? "text-rose-600" : "text-slate-900"}`}>{formatCents(l.amount_cents)}
                      {l.note && <div className="text-[11px] font-normal text-slate-400">{l.note}</div>}
                    </td>
                  </tr>
                ))}
                {(q.data?.items ?? []).length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">这一期还没有订单</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export function PlatformSettlementsPanel() {
  const qc = useQueryClient();
  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();
  const q = useQuery({ queryKey: ["finance", "platform-settlements", "list"], queryFn: () => platformSettlementApi.list() });
  const [payRow, setPayRow] = useState<SettlementRow | null>(null);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const resolve = useMutation({
    mutationFn: async (r: SettlementRow) => {
      const reason = await askReason({
        scene: "platform_settlement_diff", required: true, danger: false, title: `核差额 · ${r.platform_label}`,
        subtitle: `${periodLabel(r.period_start, r.period_end)}：应收 ${formatCents(r.net_cents, r.currency)}，实收 ${formatCents(r.paid_cents, r.currency)}，差额 ${formatCents(Math.abs(r.diff_cents), r.currency)}`, confirmText: "确认核完",
      });
      if (!reason) throw new Error("__cancel__");
      return platformSettlementApi.resolveDiff(r.id, reason);
    },
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ["finance", "platform-settlements"] }); },
    onError: (e) => { if ((e as Error).message !== "__cancel__") setErr((e as Error).message || "核差额失败"); },
  });

  const sum = q.data?.summary;
  const items = q.data?.items ?? [];
  const stats = [
    { label: "待到账", value: formatCents(sum?.pending_cents ?? 0), cls: "" },
    { label: "逾期", value: formatCents(sum?.overdue_cents ?? 0), cls: "text-rose-600" },
    { label: "本月已到账", value: formatCents(sum?.paid_this_month_cents ?? 0), cls: "" },
    { label: "差额待核", value: formatCents(sum?.diff_cents ?? 0), cls: "text-amber-600" },
  ];

  return (
    <div className="flex flex-col gap-3" data-testid="platform-settlements">
      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-[10px] border border-slate-200 bg-white px-3 py-2.5">
            <div className="text-[12px] text-slate-400">{s.label}</div>
            <div className={`text-[18px] font-bold tabular-nums ${s.cls}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {q.isLoading ? <LoadingState /> : q.isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50/50 p-6 text-center">
          <p className="text-[13px] font-semibold text-red-700">平台账期加载失败</p>
          <button onClick={() => q.refetch()} className="mt-2 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700">点击重试</button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-[12px] font-medium text-slate-400">
                <th className="px-3 py-2.5">平台</th><th>期间（美西）</th><th>订单数</th><th className="text-right">应收净额</th><th className="pl-4">预计到账</th><th className="text-right">实收</th><th className="pl-4">状态</th><th />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid="settlement-row">
                  <td className="px-3 py-2.5 font-medium text-slate-900">{r.platform_label}</td>
                  <td className="whitespace-nowrap tabular-nums">{periodLabel(r.period_start, r.period_end)}</td>
                  <td className="tabular-nums">{r.order_count}</td>
                  <td className="text-right tabular-nums">{formatCents(r.net_cents, r.currency)}</td>
                  <td className="whitespace-nowrap pl-4 tabular-nums">{r.due_date.slice(5)}</td>
                  <td className="text-right tabular-nums">{r.paid_cents == null ? "—" : formatCents(r.paid_cents, r.currency)}</td>
                  <td className="whitespace-nowrap pl-4"><span className={`rounded-full px-2.5 py-0.5 text-[12px] font-medium ${STATUS_STYLE[r.status]}`}>{statusText(r)}</span></td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    <button type="button" onClick={() => setDrawerId(r.id)} className="mr-1.5 rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50">订单明细</button>
                    <Can perm="finance.receivables.manage">
                      {r.can_record_payment && <button type="button" data-testid="record-payment" onClick={() => setPayRow(r)} className="rounded-md bg-blue-600 px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-blue-700">登记到账</button>}
                      {r.can_resolve_diff && <button type="button" data-testid="resolve-diff" disabled={resolve.isPending} onClick={() => resolve.mutate(r)} className="rounded-md border border-slate-300 px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">核差额</button>}
                    </Can>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">还没有账期。到「渠道中心 › 平台 › 结算设置」给 GYG / Viator 设好结算方式后，系统按真实订单自动生成。</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {err && <div className="text-[12.5px] text-rose-600">{err}</div>}
      <div className="text-[12.5px] text-slate-400">「登记到账」：填实收金额、到账日期、平台付款单号；实收 ≠ 应收时标「差额待核」，核完填原因（走原因弹窗）。期内订单后来被取消 / 改价的，自动进下一期的「调整」行，不改已出账的期。</div>

      {payRow && <PaymentModal row={payRow} onClose={() => setPayRow(null)} />}
      {drawerId && <OrdersDrawer id={drawerId} onClose={() => setDrawerId(null)} />}
      {reasonDialog}
    </div>
  );
}
